import { FC } from 'react';
import { Table } from 'shared/ui/table/table';
import { TablePagination } from 'shared/ui/table/table-pagination';
import { TableColumn } from 'shared/ui/table/table.types';
import { Chip } from 'shared/ui/chip/chip';
import IconButton from 'shared/ui/iconButton/icon-button';
import { ICONS } from 'shared/constants/images';
import { useTranslation } from 'hooks/api/useTranslation';
import { BILLING_TRANSACTIONS_PAGE_SIZE } from 'shared/constants/billing';
import {
  BillingTransaction,
  BillingTransactionStatus,
} from 'store/api/apiTypes';
import cls from './billing-history-table.module.scss';

interface BillingHistoryTableProps {
  transactions: BillingTransaction[];
  filteredCount: number;
  page: number;
  hasActiveSearch?: boolean;
  isLoading?: boolean;
  onPageChange: (page: number) => void;
}

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const STATUS_CHIP_COLOR: Record<BillingTransactionStatus, 'green' | 'red'> = {
  [BillingTransactionStatus.PAID]: 'green',
  [BillingTransactionStatus.FAILED]: 'red',
};

export const BillingHistoryTable: FC<BillingHistoryTableProps> = ({
  transactions,
  filteredCount,
  page,
  hasActiveSearch,
  isLoading,
  onPageChange,
}) => {
  const { t } = useTranslation();

  const columns: TableColumn<BillingTransaction>[] = [
    {
      id: 'date',
      header: t('billingDate'),
      width: '14%',
      renderCell: txn => formatDate(txn.date_created),
    },
    {
      id: 'subscription',
      header: t('billingSubscription'),
      width: '28%',
      renderCell: txn => txn.subscription,
    },
    {
      id: 'paymentMethod',
      header: t('billingPaymentMethod'),
      width: '22%',
      renderCell: txn => txn.payment_method,
    },
    {
      id: 'amount',
      header: t('billingAmount'),
      width: '12%',
      align: 'right',
      renderCell: txn => `$${txn.amount.toFixed(2)}`,
    },
    {
      id: 'status',
      header: t('billingStatus'),
      width: '14%',
      renderCell: txn => (
        <Chip color={STATUS_CHIP_COLOR[txn.status]} compact>
          {t(txn.status)}
        </Chip>
      ),
    },
    {
      id: 'invoice',
      header: t('billingInvoice'),
      width: '10%',
      align: 'center',
      className: cls.invoiceCell,
      renderCell: txn => (
        <IconButton
          dense
          pathToIcon={ICONS.download}
          aria-label={t('billingDownloadInvoice')}
          onClick={() =>
            window.open(txn.invoice_url, '_blank', 'noopener,noreferrer')
          }
        />
      ),
    },
  ];

  return (
    <>
      <div className={cls.tableWrapper}>
        <Table
          columns={columns}
          rows={transactions}
          getRowKey={txn => txn.invoice_number}
          isLoading={isLoading}
          emptyState={
            <p>
              {hasActiveSearch
                ? t('billingNoSearchResults')
                : t('billingNoTransactions')}
            </p>
          }
        />
      </div>
      <TablePagination
        page={page}
        pageSize={BILLING_TRANSACTIONS_PAGE_SIZE}
        total={filteredCount}
        onPageChange={onPageChange}
      />
    </>
  );
};
